import { ArrowForwardIcon, Search2Icon } from '@chakra-ui/icons'
import { Box, Center, Flex, HStack, IconButton, Text, Tooltip, VStack } from '@chakra-ui/react'
import {
  PopUpMessageContext,
  SearchBarContext,
  SearchBarContextData,
  usePopulatedMessage,
  useThreadSearch,
  useThreadView,
} from '@hb/react'
import { colors, ErrorWithMessage, ThreadSearchResult } from '@hb/shared'
import {
  forwardRef,
  JSX,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { searchThread } from '../../../backend/functions/other'
import { SearchBar } from '../../DataList/SearchBar'
import { Loading } from '../../Loading'
import { CollapseHorizontal } from '../../shared'
import { THREAD_HEADER_HEIGHT } from './constants'
import { useWebThreadView } from './context'
import { ThreadMessageView } from './Messages/ThreadMessageView'
const MessageSearchResult = ({
  result,
  onClick,
}: {
  result: ThreadSearchResult
  onClick: () => void
}) => {
  const [hovered, setHovered] = useState(false)
  const populatedMessage = usePopulatedMessage(result.message)
  return (
    <HStack
      onClick={onClick}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
      cursor="pointer"
      py={1}
      borderRadius={6}
      _hover={{ bg: 'blackAlpha.100' }}
      w="100%">
      <Box minW="0" flex={1}>
        <ThreadMessageView
          isPreview
          horizontal
          message={populatedMessage}
          scrollToMessage={() => false}
        />
      </Box>
      <CollapseHorizontal width={40} in={hovered}>
        <Center w="100%">
          <ArrowForwardIcon color="gray.500" />
        </Center>
      </CollapseHorizontal>
    </HStack>
  )
}

export const ThreadSearch = forwardRef<HTMLInputElement>((_, ref) => {
  const { isOpen, onClose } = useThreadSearch()
  const {
    threadId,
    threadType,
    messages: { selectMessage },
  } = useThreadView()
  const { bodyHeight } = useWebThreadView()
  const inputRef = useRef<HTMLInputElement>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [results, setResults] = useState<ThreadSearchResult[] | null>(null)
  const [loadingResults, setLoadingResults] = useState(false)
  const [resultsPage, setResultsPage] = useState(0)

  const { showError } = useContext(PopUpMessageContext)

  const handleSearch = useCallback(async () => {
    setLoadingResults(true)
    try {
      const res = await searchThread({ threadId, threadType, query: searchQuery })
      setResults(res.data)
    } catch (e) {
      showError((e as ErrorWithMessage).message ?? 'An error occurred')
    } finally {
      setLoadingResults(false)
    }
  }, [threadId, threadType, searchQuery, showError])

  // const debounced = useDebounce(searchQuery, 300)
  const [focused, setFocusedState] = useState(false)
  const contextData = useMemo<SearchBarContextData<HTMLInputElement>>(
    () => ({
      inputRef,
      query: searchQuery,
      updateQuery: (s: string) => {
        setSearchQuery(s)
        setResultsPage(0)
      },
      searchQuery,
      focused,
      setFocusedState,
    }),
    [searchQuery, focused],
  )

  const displayedResults = useMemo(
    () => (results ? results.slice(resultsPage * 10, (resultsPage + 1) * 10) : null),
    [results, resultsPage],
  )
  const handleClose = useCallback(() => {
    setSearchQuery('')
    setResults(null)
    onClose()
  }, [onClose])

  // close on escape and prevent default escape behavior
  useEffect(() => {
    if (!focused) return () => {}
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        handleClose()
      } else if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        handleSearch()
      }
    }
    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [handleClose, handleSearch, focused])

  const numPages = useMemo(() => (results ? Math.ceil(results.length / 10) : 0), [results])

  let body: JSX.Element | null = null
  if (loadingResults) {
    body = (
      <Center w="100%">
        <Loading text="Loading results..." />
      </Center>
    )
  } else if (!displayedResults) {
    body = (
      <Center w="100%">
        <Text color="gray.500">
          {searchQuery ? 'Press enter to search' : 'Search for messages'}
        </Text>
      </Center>
    )
  } else if (displayedResults.length) {
    body = (
      <Flex direction="column" w="100%">
        <Center px={2}>
          {numPages > 1 ? (
            <IconButton
              aria-label="Prev"
              size="sm"
              onClick={() => setResultsPage(p => Math.max(0, p - 1))}
              isDisabled={resultsPage === 0}>
              <ArrowForwardIcon transform="rotate(180deg)" />
            </IconButton>
          ) : null}
          <Text flex={1} textAlign="center" color="gray.500">
            {numPages > 1
              ? `Showing results ${resultsPage * 10 + 1} - ${Math.min((resultsPage + 1) * 10, results?.length ?? 0)} of ${results?.length ?? 0}`
              : `${results?.length ?? 0} results`}
          </Text>
          {numPages > 1 ? (
            <IconButton
              aria-label="Next"
              size="sm"
              onClick={() => setResultsPage(p => Math.min(numPages - 1, p + 1))}
              isDisabled={resultsPage === numPages - 1}>
              <ArrowForwardIcon />
            </IconButton>
          ) : null}
        </Center>
        {displayedResults.map(m => (
          <MessageSearchResult
            result={m}
            key={m.messageId}
            onClick={() => {
              handleClose()
              selectMessage({
                messageGroupId: m.messageGroupId,
                messageId: m.messageId,
                selectedOn: Date.now(),
                messageGroupCreatedOn: m.message.messageGroupCreatedOn,
              }) //`${m.createdOn}`)
            }}
          />
        ))}
      </Flex>
    )
  } else {
    body = (
      <Center py={2}>
        <Text color="gray.500">No matches found</Text>
      </Center>
    )
  }

  return (
    <VStack
      top={`${THREAD_HEADER_HEIGHT}px`}
      bg="blackAlpha.500"
      position="absolute"
      transition="all 300ms"
      height={`${bodyHeight}px`}
      pointerEvents={isOpen ? 'auto' : 'none'}
      opacity={isOpen ? 1 : 0}
      w="100%"
      p={2}>
      <HStack
        bg="gray.100"
        borderRadius={6}
        boxShadow="1px 1px 3px #00000077"
        spacing={0}
        pr={2}
        pl={2}
        width="100%">
        <Box flex={1}>
          <SearchBarContext.Provider value={contextData}>
            <SearchBar
              ref={ref}
              onClear={handleClose}
              flexProps={{ px: 0 }}
              autoSubmit
              itemName="message"
            />
          </SearchBarContext.Provider>
        </Box>
      </HStack>
      <VStack
        w="100%"
        py={2}
        flex={displayedResults?.length ? 1 : 'unset'}
        minH="0"
        px={1}
        overflowY="auto"
        opacity={isOpen && searchQuery ? 1 : 0}
        transition="all 300ms"
        borderRadius={6}
        bg="gray.100"
        boxShadow="1px 1px 3px #00000077">
        {body}
      </VStack>
    </VStack>
  )
})

export const ThreadSearchButton = () => {
  const { onClose, isOpen, onOpen } = useThreadSearch()
  // const debounced = useDebounce(searchQuery, 500)

  const { addingMembers } = useThreadView()
  if (addingMembers) return null
  return (
    <Box>
      <Tooltip hasArrow label="Search messages" aria-label="Search messages" placement="right">
        <IconButton
          icon={<Search2Icon position="relative" bottom="2px" width={4} height={4} />}
          transition="all 300ms"
          bg={isOpen ? colors.green.hex : 'gray.50'}
          color={isOpen ? 'white' : 'gray.500'}
          borderRadius={6}
          alignItems="center"
          justifyContent="center"
          opacity={addingMembers ? 0 : 1}
          filter="drop-shadow(1px 1px 3px #00000077)"
          variant="unstyled"
          onClick={isOpen ? onClose : onOpen}
          aria-label="Search"
        />
      </Tooltip>
    </Box>
  )
}
